import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter, lintGutter } from '@codemirror/lint'
import { Param } from '@pubstack/common/src/prebidModule'
import { BaseRadioGroup } from '@pubstack/ui/components/RadioGroup'
import { cn } from '@pubstack/ui/utils/cn'
import CodeMirror from '@uiw/react-codemirror'
import { FunctionComponent, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { Control, Controller, FieldArrayPath, FieldErrors, FieldPath, useFieldArray, UseFormSetValue, UseFormUnregister, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { ChipListInput } from '~/components/ChipListInput'
import { Icon } from '~/components/Icon'
import { Input } from '~/components/Input'
import { Select } from '~/components/Select'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { JSON_SPACE_COUNT } from '~/types/utils'
import { ClassicFormPayload } from './CollapsiblePrebidModule'
import styles from './PrebidModuleParamInput.module.css'

/** Roboto 14/22, the `P2` of the emotion palette. */
const P2 = 'font-roboto text-sm leading-[1.375rem]'

const INVALID_JSON_MESSAGE = 'Invalid JSON'
const REQUIRED_MESSAGE = 'Required value'

// same editing experience as the poc overrides editor, see JSONEditorInput
const JSON_EDITOR_EXTENSIONS = [json(), linter(jsonParseLinter(), { delay: 150 }), lintGutter()]

// paths reach arbitrarily deep into repeatable groups (config.moduleData.params.eids.0.uids.1.ext.stype),
// so errors are read by walking the tree rather than by splitting on a fixed number of segments
const errorAt = (errors: FieldErrors<ClassicFormPayload>, path: string): string | undefined => {
  const node = path.split('.').reduce<unknown>((acc, key) => (acc as Record<string, unknown> | undefined)?.[key], errors)
  return (node as { message?: string } | undefined)?.message
}

const getIn = (source: unknown, path: string): unknown => path.split('.').reduce<unknown>((acc, key) => (acc as Record<string, unknown> | undefined)?.[key], source)

/**
 * The one notion of "filled" in a module form: it decides both what a required param rejects and which `oneOf`
 * branch a saved config is showing. A field the user never reached holds `undefined` rather than `''`, which is why
 * this is not a `!== ''` check — reading the two as different states is what let an empty required param through.
 */
const hasValue = (value: unknown): boolean => (Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '')

export const variantHasValue = (variant: { fields: Record<string, Param> }, parentValue: unknown): boolean => Object.keys(variant.fields).some((key) => hasValue(getIn(parentValue, key)))

/** Weaker than `variantHasValue`: the branch is the one the config is shaped like, even with every field emptied. */
const variantHasKey = (variant: { fields: Record<string, Param> }, parentValue: unknown): boolean => Object.keys(variant.fields).some((key) => getIn(parentValue, key) !== undefined)

const setIn = (target: Record<string, unknown>, path: string, value: unknown): void => {
  const keys = path.split('.')
  const lastKey = keys.pop() as string
  const parent = keys.reduce<Record<string, unknown>>((acc, key) => {
    acc[key] = acc[key] ?? {}
    return acc[key] as Record<string, unknown>
  }, target)
  parent[lastKey] = value
}

const emptyValueFor = (param: Param): unknown => {
  switch (param.type) {
    case 'boolean':
      return false
    case 'array':
    case 'objectList':
      return []
    case 'json':
      return undefined
    default:
      // an optional param left alone must not reach prebid at all, and `''` would: `atype: ''` is not a missing atype
      return param.optional ? undefined : ''
  }
}

const emptyItemFor = (fields: Record<string, Param>): Record<string, unknown> => {
  const item: Record<string, unknown> = {}
  Object.entries(fields).forEach(([key, field]) => setIn(item, key, emptyValueFor(field)))
  return item
}

const stringifyJsonParam = (value: unknown): string => (typeof value === 'string' ? value : value === undefined || value === null ? '' : JSON.stringify(value, undefined, JSON_SPACE_COUNT))

/**
 * The info icon sitting next to a param. Tooltip's trigger is `display: inline`, so the inline-flex Icon aligns on the
 * text baseline and reads a few pixels too high; the flex wrapper pulls it out of the line box, letting the enclosing
 * `align-items: center` actually centre it. Passing a Tailwind class to the Tooltip cannot do this — Emotion injects
 * unlayered, which outranks `layer(utilities)`.
 */
const ParamInfoIcon = ({ sublabel }: { sublabel: string }) => (
  <Tooltip title={sublabel} align={'start'} reposition>
    <div className={'flex items-center'}>
      <Icon fill={Colors.Hurricane} width={'16px'} name={'info'} />
    </div>
  </Tooltip>
)

/** The column a `json`, an `objectList` or a `oneOf` param lays its header, its fields and its error out in. */
const BlockInput: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'flex flex-col gap-1 w-full'}>{children}</div>

/** The name of such a param, and the tooltip explaining it. */
const BlockHeader: FunctionComponent<{ label: string; sublabel: string }> = ({ label, sublabel }) => (
  <div className={'flex flex-row gap-1.5 items-center'}>
    <span className={cn(P2, 'text-slate-grey')}>{label}</span>
    <ParamInfoIcon sublabel={sublabel} />
  </div>
)

const BlockError: FunctionComponent<{ message: string }> = ({ message }) => (
  <span className={cn(P2, 'inline-flex items-center gap-1 text-alert')}>
    <Icon width={'16px'} name={'alert'} />
    <span>{message}</span>
  </span>
)

type JsonParamInputProps = {
  label: string
  sublabel: string
  disabled?: boolean
  error?: string
  value: unknown
  onChange: (value: unknown) => void
}

/**
 * Edits a `json` param. The raw text is kept locally so re-serialization does not fight the user while typing,
 * and the parsed value is what lands in the form: consumers get a real array/object, not a string.
 * Text that does not parse is pushed up as-is so the enclosing `rules.validate` can reject it — which is also why a
 * bare JSON string (`"foo"`) is not a valid value for a `json` param, use the `string` type for that.
 */
const JsonParamInput = ({ label, sublabel, disabled, error, value, onChange }: JsonParamInputProps) => {
  const [text, setText] = useState(() => stringifyJsonParam(value))
  const pushedValue = useRef(value)

  useEffect(() => {
    // the form was reset or the JSON mode was applied: adopt the incoming value instead of the text being edited
    if (value !== pushedValue.current) {
      pushedValue.current = value
      setText(stringifyJsonParam(value))
    }
  }, [value])

  return (
    <BlockInput>
      <BlockHeader label={label} sublabel={sublabel} />
      <div className={cn(styles.editor, 'border rounded-[3px] overflow-hidden', error ? 'border-alert' : 'border-platinum')}>
        <CodeMirror
          value={text}
          extensions={JSON_EDITOR_EXTENSIONS}
          editable={!disabled}
          readOnly={disabled}
          theme={disabled ? 'light' : undefined}
          onChange={(raw) => {
            setText(raw)
            let parsed: unknown
            try {
              parsed = JSON.parse(raw)
            } catch {
              parsed = raw
            }
            pushedValue.current = parsed
            onChange(parsed)
          }}
        />
      </div>
      {error && <BlockError message={error} />}
    </BlockInput>
  )
}

export type PrebidModuleParamInputProps = {
  control: Control<ClassicFormPayload>
  setValue: UseFormSetValue<ClassicFormPayload>
  unregister: UseFormUnregister<ClassicFormPayload>
  errors: FieldErrors<ClassicFormPayload>
  /** full react-hook-form path of the value, e.g. `config.moduleData.params.eids.0.source` */
  name: string
  param: Param
  isForbidden?: boolean
}

const OneOfInput = ({ control, setValue, unregister, errors, name, param, isForbidden }: PrebidModuleParamInputProps & { param: Extract<Param, { type: 'oneOf' }> }) => {
  const value = useWatch({ control, name: name as FieldPath<ClassicFormPayload> })
  const variantKeys = Object.keys(param.variants)
  const [pickedVariant, setPickedVariant] = useState<string | undefined>(undefined)
  // what the branches left behind were holding, keyed by `variant.field`. `unregister` drops the value along with the
  // validation rules, so without this a round trip through another variant would come back to an empty form.
  const [stashed, setStashed] = useState<Record<string, unknown>>({})
  /*
   * Until the user picks, the variant is the one the saved config carries — by its values first, and by the keys it
   * declares when they are all empty. That second pass is what keeps the branch in place while it is being edited:
   * clearing the only field of the shown branch used to leave no branch holding a value, sending the form back to
   * the first variant and unmounting the input mid-edit. It stays a derivation rather than state because the per
   * site form is rendered before its config arrives, and the branch has to follow it when it does.
   */
  const selected = pickedVariant ?? variantKeys.find((key) => variantHasValue(param.variants[key], value)) ?? variantKeys.find((key) => variantHasKey(param.variants[key], value)) ?? variantKeys[0]

  return (
    <BlockInput>
      <BlockHeader label={param.label} sublabel={param.sublabel} />
      {/* self-start keeps the group at its content width, BlockInput being a stretching column */}
      <BaseRadioGroup
        className={'self-start'}
        value={selected}
        options={variantKeys.map((key) => ({
          value: key,
          label: param.variants[key].label,
          disabled: param.disabled || isForbidden,
          trackingLabel: 'adm-integrations-modules-setting-variant-set',
        }))}
        onChange={(picked) => {
          const key = picked as string
          /* The group reports a click on the option already shown, and going through with it would stash the other
             branches as `undefined` — losing what they were keeping — and re-apply the stash over the edit in
             progress. Picking the branch already on screen is not a switch. */
          if (key === selected) {
            return
          }
          const leaving = variantKeys.filter((other) => other !== key)
          setStashed((current) => ({
            ...current,
            ...Object.fromEntries(leaving.flatMap((other) => Object.keys(param.variants[other].fields).map((field) => [`${other}.${field}`, getIn(value, field)]))),
          }))
          // drop the values and the validation rules of the branches being left behind, so the discarded shape
          // reaches neither the saved config nor isValid
          leaving.forEach((other) => unregister(Object.keys(param.variants[other].fields).map((field) => `${name}.${field}`) as FieldPath<ClassicFormPayload>[]))
          // and put back what this branch held last time it was left, before its inputs remount and read their value
          Object.keys(param.variants[key].fields).forEach((field) => {
            const previous = stashed[`${key}.${field}`]
            if (previous !== undefined) {
              setValue(`${name}.${field}` as FieldPath<ClassicFormPayload>, previous as never, { shouldDirty: true, shouldValidate: true })
            }
          })
          setPickedVariant(key)
        }}
      />
      {/* mt-3 on top of BlockInput's own 4px gap, so the variant fields sit one 16px step below the radio */}
      <div className={'flex flex-col gap-4 mt-3'}>
        {Object.entries(param.variants[selected].fields).map(([key, variantParam]) => (
          <PrebidModuleParamInput
            key={`${selected}.${key}`}
            control={control}
            setValue={setValue}
            unregister={unregister}
            errors={errors}
            name={`${name}.${key}`}
            param={variantParam}
            isForbidden={isForbidden}
          />
        ))}
      </div>
    </BlockInput>
  )
}

const ObjectListInput = ({ control, setValue, unregister, errors, name, param, isForbidden }: PrebidModuleParamInputProps & { param: Extract<Param, { type: 'objectList' }> }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as FieldArrayPath<ClassicFormPayload>,

    rules: {
      validate: (value) => (param.optional || (value as unknown[])?.length ? true : `At least one ${param.itemLabel.toLowerCase()} is required.`),
    },
  })
  const error = errorAt(errors, `${name}.root`)

  return (
    <BlockInput>
      <BlockHeader label={param.label} sublabel={param.sublabel} />
      <div className={'flex flex-col gap-3'}>
        {fields.map((field, index) => (
          <div key={field.id} className={'flex flex-col gap-3 p-3 border border-platinum rounded-[3px]'}>
            <div className={'flex flex-row justify-between items-center'}>
              <span className={cn(P2, 'font-bold')}>{`${param.itemLabel} ${index + 1}`}</span>
              <Button
                variant={'tertiary'}
                iconName={'delete'}
                disabled={param.disabled || isForbidden}
                onClick={() => remove(index)}
                trackingLabel={'adm-integrations-modules-setting-object-list-item-delete'}
              />
            </div>
            <div className={'flex flex-col gap-4'}>
              {Object.entries(param.fields).map(([key, itemParam]) => (
                <PrebidModuleParamInput
                  key={key}
                  control={control}
                  setValue={setValue}
                  unregister={unregister}
                  errors={errors}
                  name={`${name}.${index}.${key}`}
                  param={itemParam}
                  isForbidden={isForbidden}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* mt-2 on top of BlockInput's own 4px gap, so the button sits the same 12px below the last item as the items are apart */}
      <div className={'flex flex-row mt-2'}>
        <Button
          variant={'secondary'}
          iconName={'add'}
          disabled={param.disabled || isForbidden}
          onClick={() => append(emptyItemFor(param.fields) as never)}
          trackingLabel={'adm-integrations-modules-setting-object-list-item-new'}
        >
          {`Add ${param.itemLabel.toLowerCase()}`}
        </Button>
      </div>
      {error && <BlockError message={error} />}
    </BlockInput>
  )
}

/**
 * Renders one catalog param at `name`. Recurses through `objectList` and `oneOf` params, so the shape of the form is
 * entirely driven by the catalog entry and no module needs its own component.
 */
export const PrebidModuleParamInput = (props: PrebidModuleParamInputProps) => {
  // unregister is only read by the nested renderers, which receive it through props
  const { control, setValue, errors, name, param, isForbidden } = props
  const path = name as FieldPath<ClassicFormPayload>
  const error = errorAt(errors, name)
  const disabled = param.disabled || isForbidden
  const required = !param.optional

  if (param.type === 'objectList') {
    return <ObjectListInput {...props} param={param} />
  }

  if (param.type === 'oneOf') {
    return <OneOfInput {...props} param={param} />
  }

  if (param.type === 'json') {
    return (
      <Controller
        control={control}
        name={path}
        rules={{
          validate: (value) => {
            if (typeof value === 'string') {
              return INVALID_JSON_MESSAGE
            }
            if (value === undefined || value === null || Object.keys(value).length === 0) {
              return required ? REQUIRED_MESSAGE : true
            }
            return true
          },
        }}
        render={({ field: { onChange, value } }) => <JsonParamInput label={param.label} sublabel={param.sublabel} disabled={disabled} error={error} value={value} onChange={onChange} />}
      />
    )
  }

  const input =
    param.type === 'string' ? (
      <Input control={control} name={path} disabled={disabled} label={param.label} errorFromField rules={{ validate: (value) => (hasValue(value) || !required ? true : REQUIRED_MESSAGE) }} />
    ) : param.type === 'number' ? (
      <Input
        control={control}
        name={path}
        disabled={disabled}
        label={param.label}
        onChange={(t) => {
          setValue(path, !t.target.value || isNaN(Number(t.target.value)) ? '' : Number(t.target.value), { shouldDirty: true, shouldValidate: true })
        }}
        errorFromField
        rules={{ validate: (value) => (hasValue(value) ? (isNaN(Number(value)) ? 'Incorrect value' : true) : required ? REQUIRED_MESSAGE : true) }}
      />
    ) : param.type === 'select' ? (
      <Select
        control={control}
        name={path}
        disabled={disabled}
        label={param.label}
        error={error}
        options={param.options.map((option) => ({ value: option, label: option, trackingLabel: 'adm-integrations-modules-setting-select-set' as const }))}
        rules={{ required: required && REQUIRED_MESSAGE }}
      />
    ) : param.type === 'array' ? (
      <div className={'w-150'}>
        <ChipListInput
          className={styles.chipList}
          control={control}
          name={path}
          disabled={disabled}
          error={error}
          label={param.label}
          chipColor={Colors.Turquoise}
          rules={{ required: { value: required, message: 'Value is required.' } }}
        />
      </div>
    ) : (
      <Controller
        control={control}
        name={path}
        disabled={disabled}
        render={({ field: { onChange, value } }) => (
          <div className={'flex flex-row gap-2'}>
            <Toggle
              name={path}
              value={!!value}
              onClick={() => {
                onChange(!value)
              }}
              trackingLabel={value ? 'adm-integrations-modules-boolean-set' : 'adm-integrations-modules-boolean-unset'}
            />
            <p>{param.label}</p>
          </div>
        )}
      />
    )

  return (
    <div className={'flex flex-row gap-1.5 items-center'}>
      {input}
      <ParamInfoIcon sublabel={param.sublabel} />
    </div>
  )
}
